import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {HTMLElement|null} */
  #pointer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#initPointer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#destroyPointer();
  }

  /* @custom: 45R-style custom pointer on cart drawer overlay (desktop only) */
  #initPointer() {
    if (window.innerWidth < 750) return;

    this.#pointer = document.createElement('div');
    this.#pointer.className = 'r45-cart-drawer-pointer';
    this.#pointer.setAttribute('popover', 'manual');
    this.#pointer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(this.#pointer);

    document.addEventListener('mousemove', this.#handlePointerMove);
    document.addEventListener('mouseleave', this.#handlePointerLeave);

    // Promote pointer to top layer when dialog opens
    this.addEventListener(DialogOpenEvent.eventName, this.#showPointerLayer);

    // Wrap closeDialog to hide pointer before closing animation
    const originalCloseDialog = this.closeDialog;
    this.closeDialog = async () => {
      if (this.#pointer) {
        this.#pointer.classList.remove('show');
      }
      await originalCloseDialog();
      try {
        this.#pointer?.hidePopover();
      } catch (e) {
        /* noop */
      }
    };
  }

  #destroyPointer() {
    if (this.#pointer) {
      this.#pointer.remove();
      this.#pointer = null;
    }
    document.removeEventListener('mousemove', this.#handlePointerMove);
    document.removeEventListener('mouseleave', this.#handlePointerLeave);
  }

  #showPointerLayer = () => {
    try {
      this.#pointer?.showPopover();
    } catch (e) {
      /* noop */
    }
  };

  #handlePointerMove = (e) => {
    if (!this.#pointer || window.innerWidth < 750) return;

    const { dialog } = this.refs;
    if (!dialog?.open || dialog.classList.contains('dialog-closing')) {
      this.#pointer.classList.remove('show');
      return;
    }

    // Show pointer on overlay area, hide on dialog content
    const rect = dialog.getBoundingClientRect();
    const isOverDialog =
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom;

    if (isOverDialog) {
      this.#pointer.classList.remove('show');
    } else {
      this.#pointer.style.left = e.clientX + 'px';
      this.#pointer.style.top = e.clientY + 'px';
      this.#pointer.classList.add('show');
    }
  };

  #handlePointerLeave = () => {
    this.#pointer?.classList.remove('show');
  };
  /* @custom end */

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}